/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect, useState, useRef } from "react";
import DatePicker, { DateObject } from "react-multi-date-picker";
import DatePanel from "react-multi-date-picker/plugins/date_panel";
import "./DateRangePicker.css";
import { useTheme } from "../../../ThemeContext";
import { Typography } from "antd";

const { Text } = Typography;

const DataRangePicker = ({
  selectedRanges,
  setSelectedRanges,
  selectRandom,
  style,
  format = "MM-DD-YYYY",
  keepCalendarOpen = false,
  forceOpen = false
}) => {
  const { themeMode, currentTheme } = useTheme();
  const [minDate] = useState(new Date(new Date().setDate(new Date().getDate())));
  const [currentRange, setCurrentRange] = useState([]);
  const [isOpen, setIsOpen] = useState(forceOpen || false);
  const datePickerRef = useRef(null);

  const handleDateSelection = (selectedDates) => {
    if (!selectedDates) {
      setSelectedRanges([]);
      return;
    }

    if (selectRandom) {
      // For multiple date selection
      const dates = Array.isArray(selectedDates) ? selectedDates : [selectedDates];
      setSelectedRanges(dates.map(date => new DateObject(date)));
      setIsOpen(true);
    } else {
      // For range selection
      if (Array.isArray(selectedDates)) {
        if (selectedDates.length === 1) {
          // Start date selected
          setCurrentRange(selectedDates);
          setIsOpen(true);
        } else if (selectedDates.length === 2) {
          // Validate that end date is after start date
          if (selectedDates[1] < selectedDates[0]) {
            const temp = selectedDates[0];
            selectedDates[0] = selectedDates[1];
            selectedDates[1] = temp;
          }
          // End date selected - complete the range
          const newRange = selectedDates.map(date => new DateObject(date));
          setSelectedRanges(prev => [...prev, newRange]);
          setCurrentRange([]);
          setIsOpen(false);
        }
      }
    }
  };

  // Function to remove a specific range
  const handleRemoveRange = (indexToRemove) => {
    setSelectedRanges(prev => prev.filter((_, index) => index !== indexToRemove));
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark-mode", themeMode === "dark");
  }, [themeMode]);

  // Keep calendar open when clicking inside
  useEffect(() => {
    const handleClick = (e) => {
      const calendar = document.querySelector('.rmdp-wrapper');
      const input = document.querySelector('.date-picker-input');
      if (calendar && input) {
        if (calendar.contains(e.target) || input.contains(e.target)) {
          setIsOpen(true);
        }
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Force calendar to stay open when forceOpen is true
  useEffect(() => {
    if (forceOpen) {
      setIsOpen(true);
    }
  }, [forceOpen]);

  // Convert selectedRanges to the format expected by DatePicker
  const datePickerValue = selectRandom
    ? selectedRanges
    : currentRange.length > 0 ? currentRange : selectedRanges.length > 0 ? selectedRanges[selectedRanges.length - 1] : [];

  // Function to render the selected ranges in the calendar
  const mapDays = ({ date }) => {
    if (!date) return null;
    
    const currentDate = date.format(format);
    let styles = {};
    let className = "";

    if (selectRandom) {
      // Handle multiple date selection mode
      const isSelected = selectedRanges.some(selectedDate => 
        selectedDate && 
        selectedDate.day === date.day && 
        selectedDate.month.number === date.month.number && 
        selectedDate.year === date.year
      );

      if (isSelected) {
        return {
          className: "selected-date",
          style: { 
            backgroundColor: "#ff4e3a",
            color: "#fff",
            borderRadius: "4px"
          }
        };
      }
    } else {
      // Handle range selection mode
      for (let i = 0; i < selectedRanges.length; i++) {
        const range = selectedRanges[i];
        if (!range || !Array.isArray(range) || range.length !== 2) continue;

        if (date.unix === range[0].unix) {
          return {
            className: "selected-range-start",
            style: { 
              backgroundColor: "#ff4e3a",
              color: "#fff",
              borderRadius: "4px 0 0 4px"
            }
          };
        }

        if (date.unix === range[1].unix) {
          return {
            className: "selected-range-end",
            style: { 
              backgroundColor: "#ff4e3a",
              color: "#fff",
              borderRadius: "0 4px 4px 0"
            }
          };
        }

        if (date.unix > range[0].unix && date.unix < range[1].unix) {
          styles = {
            backgroundColor: "rgba(255, 78, 58, 0.2)",
            color: "#ff4e3a"
          };
          className = "selected-range-between";
        }
      }

      // Handle current range being selected
      if (currentRange.length === 1) {
        if (date.unix === currentRange[0].unix) {
          return {
            className: "current-range-start",
            style: { 
              backgroundColor: "#ff4e3a",
              color: "#fff",
              borderRadius: "4px 0 0 4px"
            }
          };
        }
        if (date.unix > currentRange[0].unix) {
          styles = {
            backgroundColor: "rgba(255, 78, 58, 0.1)",
            color: "#ff4e3a"
          };
          className = "potential-range-date";
        }
      }
    }

    if (className || Object.keys(styles).length > 0) {
      return {
        className,
        style: styles
      };
    }
  };

  return (
    <div className={`${themeMode}-date-picker`}>
      <div
        style={{
          textAlign: "left",
          margin: ".5rem",
          display: "flex",
          flexDirection: "column",
          background: currentTheme.callAutomationPage.dateBackground,
          borderRadius: "1vh",
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          padding: "8px",
        }}>
          <img
            style={{ marginRight: "8px" }}
            src={`/icons/calendar${themeMode === "dark" ? "-dark" : ""}.svg`}
            width={"20"}
            alt="Calendar Icon"
          />
          <DatePicker
            ref={datePickerRef}
            className={`${themeMode}-ca-date-picker`}
            inputClass="date-picker-input"
            value={datePickerValue}
            placeholder="Select Dates"
            onChange={handleDateSelection}
            multiple={selectRandom}
            range={!selectRandom}
            style={style}
            sort
            format={format}
            calendarPosition="bottom-left"
            plugins={[
              <DatePanel
                position="bottom"
                markFocused={true}
                className="table-scroll-container"
              />,
            ]}
            minDate={minDate}
            highlightToday={true}
            onOpen={() => setIsOpen(true)}
            onClose={(e) => {
              if (!forceOpen && !selectRandom && e?.type === "blur") {
                setIsOpen(false);
              }
            }}
            open={forceOpen || isOpen}
            mapDays={mapDays}
          />
        </div>
        
        {!selectRandom && selectedRanges.length > 0 && (
          <div style={{
            padding: "0px 8px 8px 8px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            maxHeight: "100px",
            overflow: "hidden",
          }}>
            <Text style={{ 
              color: currentTheme.callAutomationPage.textColor,
              marginBottom: "4px",
              fontSize: "0.9em",
              position: "sticky",
              top: 0,
              backgroundColor: currentTheme.callAutomationPage.dateBackground,
              padding: "4px 0",
              zIndex: 1
            }}>
              Selected Ranges:
            </Text>
            <div className={`selected-ranges-container ${themeMode}-mode`} style={{
              overflowY: "auto",
              paddingRight: "4px",
              marginRight: "-4px",
              scrollbarWidth: "thin",
              scrollbarColor: themeMode === "dark" 
                ? "rgba(255, 255, 255, 0.2) transparent"
                : "rgba(255, 78, 58, 0.3) transparent",
              msOverflowStyle: "-ms-autohiding-scrollbar"
            }}>
              {selectedRanges.map((range, index) => (
                <div 
                  key={index} 
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "4px 8px",
                    background: currentTheme.callAutomationPage.timeButtonClose,
                    borderRadius: "4px",
                    marginBottom: "4px",
                    minHeight: "15px",
                  }}
                >
                  <span style={{ 
                    color: currentTheme.callAutomationPage.textColor,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    marginRight: "8px"
                  }}>
                    {range[0].format(format)} - {range[1].format(format)}
                  </span>
                  <button
                    onClick={() => handleRemoveRange(index)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "4px",
                      flexShrink: 0
                    }}
                  >
                    <img 
                      src={themeMode === "dark" ? "/icons/cancel-grey.svg" : "/icons/cancel-orange.svg"}
                      width="16"
                      alt="Remove"
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataRangePicker;

